import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import MovieCard from '@/components/feature/MovieCard';
import { allContent, categoryNames } from '@/mocks/content';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = allContent.find((item) => item.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl font-bold text-foreground-900">内容未找到</h1>
            <p className="mt-4 text-foreground-600">抱歉，您要查看的内容不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedItems = allContent
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 3);

  const subLabels: Record<string, string> = {
    dianshiju: '电视剧',
    dianying: '电影',
    zongyi: '综艺',
  };

  const categoryLabel = subLabels[article.category] || '';
  const categorySlug = article.category;

  return (
    <>
      <ScrollToTop />
      <SEOSchema type="WebPage" data={{
        name: `${article.title} - 色影视`,
        description: article.description,
        url: `https://www.seyingtv.com/article/${article.id}`,
      }} />
      <SEOSchema type="Article" data={{
        headline: article.title,
        description: article.description,
        datePublished: `${article.year}-01-01`,
        author: { '@type': 'Person', name: article.director },
        publisher: { '@type': 'Organization', name: '色影视' },
      }} />
      <SEOSchema type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '色影视首页', item: 'https://www.seyingtv.com/' },
          { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://www.seyingtv.com/category/${categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: `/category/${categorySlug}` },
                { label: article.title },
              ]}
            />

            <article className="mt-6">
              {/* Header */}
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="w-full lg:w-[400px] flex-shrink-0">
                  <div className="aspect-video rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={article.imageUrl}
                      alt={`${article.title} - 色影视`}
                      title={`${article.title} - ${categoryLabel}在线观看`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {categoryLabel}
                    </span>
                    <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {article.subcategory}
                    </span>
                    {article.isHot && (
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                        热门
                      </span>
                    )}
                    {article.isNew && (
                      <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                        最新
                      </span>
                    )}
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {article.title}
                  </h1>

                  <div className="mt-4 flex items-center gap-3">
                    <span className="flex items-center gap-1 text-accent-500 font-semibold text-lg">
                      <i className="ri-star-fill"></i>
                      {article.rating}
                    </span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
                    <div className="flex items-baseline gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                      <span className="text-foreground-800">{article.director}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">年份：</span>
                      <span className="text-foreground-800">{article.year}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">地区：</span>
                      <span className="text-foreground-800">{article.region}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-foreground-500 whitespace-nowrap">
                        {article.episodes ? '集数：' : '时长：'}
                      </span>
                      <span className="text-foreground-800">
                        {article.episodes ? `共${article.episodes}集` : article.duration}
                      </span>
                    </div>
                    {article.updateInfo && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-foreground-500 whitespace-nowrap">状态：</span>
                        <span className="text-accent-600 font-medium">{article.updateInfo}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-baseline gap-2 text-sm">
                    <span className="text-foreground-500 whitespace-nowrap">主演：</span>
                    <span className="text-foreground-800">{article.cast.join(' / ')}</span>
                  </div>

                  <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                    {article.description}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="mt-10 max-w-3xl">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">剧情简介</h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                  {article.content.split('\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </article>

            {/* Related */}
            {relatedItems.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关{categoryLabel}推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedItems.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}